import { Flex, GapSize } from "@/components/atoms/flex";
import { EmailProps } from "./types";
import { Avatar, Button, Text, mergeClasses } from "@fluentui/react-components";
import { useStyle } from "./style";
import { Attach20Regular, MoreHorizontalRegular } from "@fluentui/react-icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Attachment } from "@/components/atoms/attachment";
import { formatDate } from "@/common/helpers/dateHelper";
import { decodeHtmlEntities, sanitizeEmailBody } from "@/common/helpers/emailHelper";
import { AttachmentData, Recipient } from "@/repository/models/message";
import { FilePreview } from "../filePreview";
import { fromBytesToImageSrc, isImage } from "@/common/helpers";
import { useInlineAttachments } from "./hooks";
import {
	fetchAttachment,
	fetchHistoryMessageAttachment,
	fetchHistoryMessageBody,
	fetchMessageBody,
	MessageBodyKey,
} from "@/repository/queryFns";
import { useTextMonitoring } from "@/hooks/supervisor";
import { useQuery } from "@tanstack/react-query";
import { UploadStatus } from "../emailEditor";
import { useResponsiveBreakpoints } from "@/hooks/responsive";
import { useTeamsTheme } from "@/hooks/theme";
import { TeamsThemeType } from "@/common/theme/teamsThemeType";

export const Email = (props: EmailProps) => {
	const { email, isDefaultOpen, isHistory, index, isPreview, isHighlighted } =
		props;
	const intl = useIntl();
	const classes = useStyle();
	const [isOpen, setOpenState] = useState<boolean>(isDefaultOpen);
	const [isThreadHistoryVisible, setIsThreadHistoryVisible] =
		useState<boolean>(false);
	const isDarkTheme = useTeamsTheme() === TeamsThemeType.Dark;

	const inlineAttachments = useInlineAttachments({
		sessionId: email.sessionId,
		messageId: email.id,
		isHistory,
		isPreview,
	});

	const isTextMonitoring = useTextMonitoring();

	const { isMobile } = useResponsiveBreakpoints();

	useEffect(() => {
		isOpen && inlineAttachments.trigger();
	}, [isOpen]);

	const {
		data: messageBodyWithHistory,
		isLoading: isBodyWithThreadHistoryLoading,
	} = useQuery({
		queryKey: [MessageBodyKey, email.id],
		throwOnError: true,
		queryFn: () =>
			isHistory
				? fetchHistoryMessageBody(email.sessionId, email.id)
				: fetchMessageBody(email.sessionId, isPreview, email.id),
		enabled: isHistory ? isOpen && isThreadHistoryVisible : isOpen,
	});

	const recipientsList = useCallback(
		(recipients: Recipient[], labelTranslationKey: string) => (
			<Flex gap={GapSize.Small}>
				<Text
					size={200}
					children={`${intl.formatMessage({
						id: labelTranslationKey,
					})}:`}
				/>
				<Flex gap={GapSize.ExtraSmall} wrap="wrap">
					{recipients.map((receiver) => {
						return (
							<Text
								key={receiver.address}
								size={200}
								children={`${receiver?.name} <${receiver.address}>;`}
								title={`${receiver?.name} <${receiver.address}>`}
							/>
						);
					})}
				</Flex>
			</Flex>
		),
		[]
	);

	const previewText = useMemo(() => {
		const text = decodeHtmlEntities(
			email.body.content
				.replace(/<br\s*\/?>/gi, "\n")
				.replace(/<\/(p|div|li|tr|h[1-6])>/gi, "\n")
				.replace(/<[^>]+>/g, "")
		);

		return text
			.split(/\r?\n/)
			.map((line) => line.trim())
			.filter((line) => line.length > 0)
			.slice(0, 2)
			.join("\n");
	}, [email.body.content]);

	const header = (
		<Flex
			hAlign="space-between"
			vAlign="flex-start"
			gap={GapSize.Medium}
		>
			<Flex
				vAlign="center"
				gap={GapSize.Medium}
				className={classes.hiddenOverflow}
			>
				<Avatar name={email.sender.name} />
				<Flex column gap={GapSize.Small}>
					<Flex gap={GapSize.Small} column={isMobile}>
						<Text
							weight="semibold"
							children={email.sender.name}
							title={email.sender.name}
						/>
						{isOpen && (
							<Text
								children={`<${email.sender.address}>`}
								title={`<${email.sender.address}>`}
							/>
						)}
					</Flex>
					{isOpen ? (
						<>
							{recipientsList(email.toRecipients, "Interaction.EmailEditor.To")}
							{email.ccRecipients &&
								email.ccRecipients.length > 0 &&
								recipientsList(
									email.ccRecipients,
									"Interaction.EmailEditor.Cc"
								)}
						</>
					) : (
						<Text
							size={200}
							className={classes.previewText}
							children={previewText}
						/>
					)}
				</Flex>
			</Flex>
			<Flex
				column
				hAlign="flex-end"
				vAlign="center"
				gap={GapSize.None}
			>
				{isOpen
					? props.headerActions
					: !isTextMonitoring && (
							<Attach20Regular
								className={mergeClasses(
									classes.attachHeaderIcon,
									!email.attachments.filter((x) => !x.isInline).length
										? classes.hidden
										: undefined
								)}
							/>
						)}
				<Text
					truncate
					wrap={false}
					children={formatDate(new Date(email.receivedDateTime), false)}
				/>
			</Flex>
		</Flex>
	);

	const bodyContent = useMemo(() => {
		let messageBody =
			isThreadHistoryVisible && !isBodyWithThreadHistoryLoading
				? messageBodyWithHistory
				: email.body;

		return sanitizeEmailBody(
			messageBody?.content ?? "",
			email.id,
			inlineAttachments.inline
		);
	}, [
		email.body,
		messageBodyWithHistory,
		inlineAttachments,
		isThreadHistoryVisible,
		isBodyWithThreadHistoryLoading,
	]);

	const fetchAttachmentPromise = useCallback(
		async (x: AttachmentData) => {
			let attachment;

			if (isHistory) {
				attachment = await fetchHistoryMessageAttachment(
					email.sessionId,
					email.id,
					x.id
				);
			} else {
				attachment = await fetchAttachment(
					email.sessionId,
					isPreview,
					email.id,
					x.id
				);
			}

			return fromBytesToImageSrc(
				attachment.contentType,
				attachment.contentBytes
			);
		},
		[isHistory, email, isPreview]
	);

	const body = (
		<Flex column gap={GapSize.Large} className={classes.bodyContainer}>
			<Flex gap={GapSize.Medium} wrap="wrap">
				{email.attachments
					?.filter((item: AttachmentData) => !item.isInline)
					.map((item: AttachmentData) => (
						<FilePreview
							key={item.id}
							name={item.name}
							queryKey={item.id}
							queryFn={() => {
								return fetchAttachmentPromise(item);
							}}
							children={(preview: () => void, download: () => void) => (
								<Attachment
									name={item.name}
									size={item.size}
									type={item.contentType}
									onPreview={isImage(item.contentType) ? preview : undefined}
									onDownload={download}
									uploadStatus={UploadStatus.None}
								/>
							)}
						/>
					))}
			</Flex>
			<div
				className={classes.dangerouslyContainer}
				dangerouslySetInnerHTML={{ __html: bodyContent }}
			/>
			{index > 0 && (
				<div className={classes.threadHistoryToggleWrapper}>
					<Button
						size="small"
						appearance="subtle"
						icon={<MoreHorizontalRegular />}
						title={
							isThreadHistoryVisible
								? intl.formatMessage({
										id: "Interaction.EmailEditor.HideMessageHistory",
									})
								: intl.formatMessage({
										id: "Interaction.EmailEditor.ShowMessageHistory",
									})
						}
						onClick={() => {
							setIsThreadHistoryVisible(!isThreadHistoryVisible);
						}}
						className={classes.threadHistoryToggleButton}
					/>
				</div>
			)}
			{props.footerActions}
		</Flex>
	);

	const rootContainerClassName = useMemo(() => {
		if (!isHighlighted) {
			return classes.rootContainer;
		}
		return isDarkTheme
			? classes.rootContainerHighlightedDark
			: classes.rootContainerHighlighted;
	}, [isHighlighted, isDarkTheme, classes]);

	return (
		<Flex column className={rootContainerClassName} gap={GapSize.Large}>
			<div
				className={classes.header}
				onClick={() => setOpenState((prev: boolean) => !prev)}
			>
				{header}
			</div>
			{isOpen && body}
		</Flex>
	);
};
